import { useState, useEffect } from 'react';
import { router } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { CheckCircle, Gift, User } from "lucide-react";

export default function RedemptionGiftCard({ redemption }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    router.patch(`/redemptions/${redemption.id}/gift_card`, {
      gift_codes: 'Entregado'
    }, {
      onSuccess: () => {
        // Iniciar countdown de 10 segundos
        setCountdown(10);
      },
      onFinish: () => setIsSubmitting(false)
    });
  };

  const isFulfilled = redemption.status === 'fulfilled';

  // Efecto para manejar el countdown y cerrar la ventana
  useEffect(() => {
    if (countdown === null) return;

    if (countdown === 0) {
      window.close();
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown]);

  // Si la página carga y ya está fulfilled, iniciar countdown
  useEffect(() => {
    if (isFulfilled && countdown === null) {
      setCountdown(10);
    }
  }, [isFulfilled]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-xl border-t-4 border-t-blue-500">
          <CardHeader>
            <CardTitle className="flex items-center text-2xl">
              <Gift className="w-6 h-6 mr-2 text-blue-500" />
              Verificación de Tarjeta de Regalo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Estado del Canje */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-700">Estado del Canje</h3>
                <Badge className={isFulfilled ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}>
                  {isFulfilled ? "✅ Cumplido" : "⏳ Pendiente de procesar"}
                </Badge>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center text-gray-600">
                  <User className="w-4 h-4 mr-2" />
                  <span className="font-medium">{redemption.user.full_name}</span>
                  <span className="ml-2 text-gray-400">({redemption.user.email})</span>
                </div>
                <div className="text-gray-600">
                  <strong>ID:</strong> #{redemption.id}
                </div>
                <div className="text-gray-600">
                  <strong>Puntos:</strong> {redemption.points_spent} pts
                </div>
                {redemption.reward && (
                  <div className="text-gray-600">
                    <strong>Recompensa:</strong> {redemption.reward.name}
                  </div>
                )}
              </div>
            </div>

            {/* Formulario o Estado Cumplido */}
            {isFulfilled ? (
              <div className="bg-green-50 rounded-lg p-6 border-2 border-green-200">
                <div className="flex items-center mb-3">
                  <CheckCircle className="w-6 h-6 mr-2 text-green-600" />
                  <h3 className="font-semibold text-green-800">Canje Entregado</h3>
                </div>
                <p className="text-sm text-green-700">
                  El usuario ha sido notificado de que su canje ha sido procesado.
                </p>
                {countdown !== null && (
                  <div className="mt-4 text-center">
                    <p className="text-sm text-green-600 font-medium">
                      Esta ventana se cerrará automáticamente en {countdown} segundo{countdown !== 1 ? 's' : ''}...
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 mb-4">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Al confirmar, se marcará este canje como entregado.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3"
                >
                  {isSubmitting ? 'Procesando...' : 'Redimido - Avisar al Usuario'}
                </Button>
              </form>
            )}

            {/* Info Adicional */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2">ℹ️ Instrucciones</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Verifica que el usuario ha solicitado este canje</li>
                <li>• Procesa la entrega de la tarjeta de regalo fuera del sistema</li>
                <li>• Presiona el botón para marcar como entregado</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
